import type { FaireAdCampaignEntity } from "@faire/playground-entity-db/entity_types/adCampaign";
import type { FaireAdPerformancePeriodEntity } from "@faire/playground-entity-db/entity_types/adPerformancePeriod";
import type { FaireAdvertiserProfileEntity } from "@faire/playground-entity-db/entity_types/advertiserProfile";
import type { FaireBrandEntity } from "@faire/playground-entity-db/entity_types/brand";
import { getEntityByToken } from "../../../data/get_entity";

import {
  getRealBrandActivityMonthLabels,
  getRealBrandChartMonthLabels,
  getRealBrandPeriods,
  isRealBrandProfile,
} from "./realBrandData";
import type { InsightsChartMetric, InsightsCustomerSegment } from "./types";

/** Jai Mankoo P8 — Marigold & Main (Sweet Water Decor, anonymized). */
export const P8_PROFILE_TOKEN = "adv_p8_new_returning";

/** P9 — high-utilization / budget-capped archetype (Wildflower & Co.). */
export const P9_BUDGET_CAPPED_PROFILE_TOKEN = "adv_p9_budget_capped";

/** Real-brand variants (data in realBrandData.ts). */
export const TOTALLY_BAMBOO_PROFILE_TOKEN = "adv_totally_bamboo";
export const PENGUIN_RH_PROFILE_TOKEN = "adv_penguin_rh";

/** The Good Book Company — modest-but-successful real-brand variant. */
export const GOOD_BOOK_PROFILE_TOKEN = "adv_good_book_company";

/** MALWEST design — NEW_ONLY, growing-but-early real-brand variant. */
export const MALWEST_PROFILE_TOKEN = "adv_malwest";

/** King Floyd's — NEW_AND_RETURNING, spending well under budget. */
export const KING_FLOYDS_PROFILE_TOKEN = "adv_king_floyds";

/** Oggi — NEW_ONLY, spend pinned to a rising budget with strong returns. */
export const OGGI_PROFILE_TOKEN = "adv_oggi";

/** Elana Gabrielle — NEW_AND_RETURNING, long tenure with the best reorder engine. */
export const ELANA_GABRIELLE_PROFILE_TOKEN = "adv_elana_gabrielle";

/** Kaari & Co. — NEW_AND_RETURNING, scaling new-customer spend fast. */
export const KAARI_CO_PROFILE_TOKEN = "adv_kaari_co";

/** The Woodsy Craft Co. — NEW_AND_RETURNING, small and lumpy but high ROAS. */
export const WOODSY_CRAFT_PROFILE_TOKEN = "adv_woodsy_craft";

/**
 * Zotter Chocolates — NEW_AND_RETURNING with the reorder half already stopped,
 * spending a third of its budget at the best ROAS in the set. The only variant
 * that carries its full tenure rather than a trailing year.
 */
export const ZOTTER_CHOCOLATES_PROFILE_TOKEN = "adv_zotter_chocolates";

/**
 * Dairy Brand — NEW_AND_RETURNING on a pooled $6,000 budget, and the largest
 * program here by an order of magnitude: 1,028 conversions against Zotter's 82.
 * The only variant whose reorder campaign is a standing part of the mix.
 */
export const DAIRY_BRAND_PROFILE_TOKEN = "adv_dairy_brand";

/**
 * Do Good Candle Studio — NEW_AND_RETURNING on a $250 floor budget, and the
 * emptiest program here by a wide margin: 2 conversions against Dairy Brand's
 * 1,028, both of them in Apr 2024. Reach for it when the page needs to be seen
 * with almost no data in it.
 *
 * The only variant shown under its real brand name rather than an anonymized one.
 */
export const DO_GOOD_CANDLE_PROFILE_TOKEN = "adv_do_good_candle";

/**
 * Dairy Brand is what the prototype opens on, so it's the default here too.
 * Page.tsx seeds `profileState` to the matching state number; the two are read
 * during the same first render and have to agree.
 */
let activeProfileToken = DAIRY_BRAND_PROFILE_TOKEN;

/** Legacy P8/P9 boolean switch (kept for the budget-constrained banner logic). */
export function setActiveAdvertiserProfile(budgetConstrained: boolean) {
  activeProfileToken = budgetConstrained
    ? P9_BUDGET_CAPPED_PROFILE_TOKEN
    : P8_PROFILE_TOKEN;
}

/** Set the active advertiser profile by token (P8/P9 or a real-brand variant). */
export function setActiveProfileToken(token: string) {
  activeProfileToken = token;
}

export function getActiveProfileToken(): string {
  return activeProfileToken;
}

const MONTH_LABEL = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

function formatMonthLabel(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return MONTH_LABEL.format(new Date(year, month - 1, day));
}

function formatRenewalDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

export function getP8Profile(): FaireAdvertiserProfileEntity {
  const profile = getEntityByToken(activeProfileToken, "advertiserProfile");
  if (!profile) {
    throw new Error(
      `Advertiser profile (${activeProfileToken}) not found — restart the dev server.`
    );
  }
  return profile;
}

export function getP8Brand(): FaireBrandEntity | undefined {
  return getEntityByToken(getP8Profile().brandToken, "brand");
}

export function getP8Campaigns(): FaireAdCampaignEntity[] {
  return getP8Profile()
    .campaignTokens.map((token) => getEntityByToken(token, "adCampaign"))
    .filter((campaign): campaign is FaireAdCampaignEntity => campaign != null);
}

export function getP8OverviewPeriods(): FaireAdPerformancePeriodEntity[] {
  if (isRealBrandProfile(activeProfileToken)) {
    return getRealBrandPeriods(activeProfileToken, "OVERVIEW");
  }
  return getP8Profile()
    .overviewPerformancePeriodTokens.map((token) =>
      getEntityByToken(token, "adPerformancePeriod")
    )
    .filter(
      (period): period is FaireAdPerformancePeriodEntity => period != null
    )
    .sort((left, right) => left.startDate.localeCompare(right.startDate));
}

/** Performance periods for a segment scope, real-brand-aware. */
export function getScopePeriods(
  scope: "NEW_MARKETPLACE" | "REORDER_MARKETPLACE"
): FaireAdPerformancePeriodEntity[] {
  if (isRealBrandProfile(activeProfileToken)) {
    return getRealBrandPeriods(activeProfileToken, scope);
  }
  const campaign = getP8Campaigns().find((entry) => entry.segment === scope);
  return (campaign?.performancePeriodTokens ?? [])
    .map((token) => getEntityByToken(token, "adPerformancePeriod"))
    .filter(
      (period): period is FaireAdPerformancePeriodEntity => period != null
    )
    .sort((left, right) => left.startDate.localeCompare(right.startDate));
}

export type P8MonthlyTotals = {
  month: string;
  adSales: number;
  adSpend: number;
  impressions: number;
  clicks: number;
  conversions: number;
};

function seedRand(n: number) {
  const x = Math.sin(n * 9301 + 49297) * 10000;
  return x - Math.floor(x);
}

/** Last 12 complete months ending Jun 2026; Apr–Jun use P8 overview receipts. */
export function getP8ChartMonths(): string[] {
  if (isRealBrandProfile(activeProfileToken)) {
    return getRealBrandChartMonthLabels(activeProfileToken);
  }
  const labels: string[] = [];
  for (let i = 11; i >= 0; i -= 1) {
    const date = new Date(2026, 5 - i, 1);
    labels.push(MONTH_LABEL.format(date));
  }
  return labels;
}

/** Every complete activity month from earliest campaign start through the latest receipt. */
export function getP8ActivityMonthLabels(): string[] {
  if (isRealBrandProfile(activeProfileToken)) {
    return getRealBrandActivityMonthLabels(activeProfileToken);
  }
  const campaigns = getP8Campaigns();
  const startDates = campaigns
    .map((campaign) => campaign.startedRunningAt)
    .filter((value): value is string => Boolean(value))
    .sort();
  const [startYear, startMonth] = (startDates[0] ?? "2023-10-01")
    .split("-")
    .map(Number);

  const lastPeriod = getP8OverviewPeriods().at(-1);
  const endYear = lastPeriod
    ? Number(lastPeriod.startDate.split("-")[0])
    : 2026;
  const endMonth = lastPeriod ? Number(lastPeriod.startDate.split("-")[1]) : 6;

  const labels: string[] = [];
  let year = startYear;
  let month = startMonth;
  while (year < endYear || (year === endYear && month <= endMonth)) {
    labels.push(MONTH_LABEL.format(new Date(year, month - 1, 1)));
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return labels;
}

function buildP8MonthlyTotals(months: string[]): P8MonthlyTotals[] {
  const anchorByMonth = new Map(
    getP8OverviewPeriods().map((period) => [
      formatMonthLabel(period.startDate),
      {
        adSales: period.salesDollars ?? 0,
        adSpend: period.adsSpendDollars,
        impressions: period.impressions,
        clicks: period.clicks,
        conversions:
          (period.newCustomersAcquired ?? 0) + (period.reorderConversions ?? 0),
      },
    ])
  );

  const anchorValues = [...anchorByMonth.values()];
  const avg = {
    adSales:
      anchorValues.reduce((sum, row) => sum + row.adSales, 0) /
      anchorValues.length,
    adSpend:
      anchorValues.reduce((sum, row) => sum + row.adSpend, 0) /
      anchorValues.length,
    impressions:
      anchorValues.reduce((sum, row) => sum + row.impressions, 0) /
      anchorValues.length,
    clicks:
      anchorValues.reduce((sum, row) => sum + row.clicks, 0) /
      anchorValues.length,
    conversions:
      anchorValues.reduce((sum, row) => sum + row.conversions, 0) /
      anchorValues.length,
  };

  return months.map((month, index) => {
    const anchor = anchorByMonth.get(month);
    if (anchor) {
      return { month, ...anchor };
    }
    // Independent wobble per field so the sales-to-spend ratio (and therefore
    // ROAS) varies month to month instead of collapsing to a constant.
    const salesWobble = 0.82 + seedRand(index + 41) * 0.36;
    const spendWobble = 0.82 + seedRand(index + 173) * 0.36;
    const impressionsWobble = 0.82 + seedRand(index + 251) * 0.36;
    const clicksWobble = 0.82 + seedRand(index + 307) * 0.36;
    const conversionsWobble = 0.82 + seedRand(index + 419) * 0.36;
    return {
      month,
      adSales: Math.round(avg.adSales * salesWobble),
      adSpend: Math.round(avg.adSpend * spendWobble),
      impressions: Math.round(avg.impressions * impressionsWobble),
      clicks: Math.round(avg.clicks * clicksWobble),
      conversions: Math.round(avg.conversions * conversionsWobble),
    };
  });
}

export function getP8MonthlyTotals(): P8MonthlyTotals[] {
  return buildP8MonthlyTotals(getP8ChartMonths());
}

export function getP8ActivityMonthlyTotals(): P8MonthlyTotals[] {
  return buildP8MonthlyTotals(getP8ActivityMonthLabels());
}

export function getP8ImpressionsPerSpendRatio(): number {
  const periods = getP8OverviewPeriods();
  const totalSpend = periods.reduce(
    (sum, period) => sum + period.adsSpendDollars,
    0
  );
  const totalImpressions = periods.reduce(
    (sum, period) => sum + period.impressions,
    0
  );
  return totalSpend > 0 ? totalImpressions / totalSpend : 10;
}

export function getP8SegmentScale(
  segment: InsightsCustomerSegment,
  metric: InsightsChartMetric
): number {
  const profile = getP8Profile();
  const overview = profile.overview;

  if (segment === "all-customers") {
    return 1;
  }

  if (metric === "ad-sales") {
    if (segment === "new-customers") {
      return (
        (overview.newCustomerAdsSalesDollars ?? 0) /
        overview.totalAdsSalesDollars
      );
    }
    return (
      (overview.reorderAdsSalesDollars ?? 0) / overview.totalAdsSalesDollars
    );
  }

  if (metric === "impressions" || metric === "clicks") {
    const field = metric;
    const periods = getP8OverviewPeriods();
    const total = periods.reduce((sum, period) => sum + period[field], 0);
    const scope =
      segment === "new-customers" ? "NEW_MARKETPLACE" : "REORDER_MARKETPLACE";
    const scopeTotal = getScopePeriods(scope).reduce(
      (sum, period) => sum + period[field],
      0
    );
    const fallback = segment === "new-customers" ? 0.33 : 0.67;
    return total > 0 ? scopeTotal / total : fallback;
  }

  const totalConversions = overview.numTotalConversions;
  if (segment === "new-customers") {
    return overview.numCustomersAcquired / totalConversions;
  }
  return overview.numReorderConversions / totalConversions;
}

export function getP8OverviewTotals() {
  const profile = getP8Profile();
  return {
    adSales: profile.overview.totalAdsSalesDollars,
    adSpend: profile.overview.totalAdsSpendDollars,
    impressions: getP8OverviewPeriods().reduce(
      (sum, period) => sum + period.impressions,
      0
    ),
    clicks: getP8OverviewPeriods().reduce(
      (sum, period) => sum + period.clicks,
      0
    ),
    conversions: profile.overview.numTotalConversions,
    roas: profile.overview.combinedFirstYearRoas ?? 0,
  };
}

export function getP8HeaderSubtitle(): string {
  const profile = getP8Profile();
  const budget = profile.budget.totalBudgetDollars.toLocaleString("en-US");
  if (profile.globalStatus === "STOPPED") {
    return `Advertising paused · last monthly budget of $${budget}`;
  }
  const renewal = profile.budget.nextRenewsAtDate
    ? formatRenewalDate(profile.budget.nextRenewsAtDate)
    : "Aug 1, 2026";
  return `Renews ${renewal} with a monthly budget of $${budget}`;
}

/** Status pill shown in the Promoted Listings header, driven by the profile. */
export function getP8HeaderStatus(): {
  label: string;
  variant: "success" | "warning";
} {
  switch (getP8Profile().globalStatus) {
    case "STOPPED":
      return { label: "Paused", variant: "warning" };
    case "SCHEDULED":
      return { label: "Scheduled", variant: "warning" };
    case "NOT_STARTED":
      return { label: "Not started", variant: "warning" };
    default:
      return { label: "Active", variant: "success" };
  }
}

export function getP8DefaultBudget() {
  const profile = getP8Profile();
  const campaigns = getP8Campaigns();
  const newCampaign = campaigns.find(
    (campaign) => campaign.segment === "NEW_MARKETPLACE"
  );
  const reorderCampaign = campaigns.find(
    (campaign) => campaign.segment === "REORDER_MARKETPLACE"
  );

  return {
    // Separate per-segment budgets only when targeting both audiences AND not
    // pooling into one shared budget (sharedBudgetDollars set → shared budget).
    separateBudget:
      profile.audienceType === "NEW_AND_RETURNING" &&
      profile.budget.sharedBudgetDollars == null,
    overallBudget: String(
      profile.budget.sharedBudgetDollars ?? profile.budget.totalBudgetDollars
    ),
    newCustomerBudget: String(newCampaign?.monthlyBudgetDollars ?? 6000),
    returningBudget: String(reorderCampaign?.monthlyBudgetDollars ?? 9000),
  };
}

/** COMPETITIVE growth mode → index 2 in GrowthModeModule variant A. */
export function getP8DefaultGrowthIdx(): number {
  const mode = getP8Profile().budget.spendingMode;
  if (mode === "CONSERVATIVE") return 0;
  if (mode === "MODERATE") return 1;
  if (mode === "COMPETITIVE") return 2;
  return 3;
}

export function getP8BrandName(): string {
  return getP8Brand()?.name ?? getP8Profile().displayName;
}

export function getP8TableSegmentScale(
  segment: InsightsCustomerSegment,
  field: "adSales" | "adSpend" | "impressions" | "clicks" | "conversions"
): number {
  const profile = getP8Profile();
  const overview = profile.overview;

  if (segment === "all-customers") {
    return 1;
  }

  if (field === "adSales") {
    return segment === "new-customers"
      ? (overview.newCustomerAdsSalesDollars ?? 0) /
          overview.totalAdsSalesDollars
      : (overview.reorderAdsSalesDollars ?? 0) / overview.totalAdsSalesDollars;
  }

  if (field === "adSpend") {
    return segment === "new-customers"
      ? (overview.newCustomerAdsSpendDollars ?? 0) /
          overview.totalAdsSpendDollars
      : (overview.reorderAdsSpendDollars ?? 0) / overview.totalAdsSpendDollars;
  }

  if (field === "conversions") {
    return segment === "new-customers"
      ? overview.numCustomersAcquired / overview.numTotalConversions
      : overview.numReorderConversions / overview.numTotalConversions;
  }

  const totals = getP8OverviewTotals();
  const key: "impressions" | "clicks" =
    field === "impressions" ? "impressions" : "clicks";
  const sumScopeField = (scope: "NEW_MARKETPLACE" | "REORDER_MARKETPLACE") =>
    getScopePeriods(scope).reduce((sum, period) => sum + period[key], 0);

  const newValue = sumScopeField("NEW_MARKETPLACE");
  const reorderValue = sumScopeField("REORDER_MARKETPLACE");
  const totalValue =
    field === "impressions" ? totals.impressions : totals.clicks;

  if (totalValue <= 0) {
    return segment === "new-customers" ? 0.33 : 0.67;
  }

  return segment === "new-customers"
    ? newValue / totalValue
    : reorderValue / totalValue;
}
